import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { 
  Phone,
  MapPin,
  Users,
  Award,
  Target,
  Eye,
  Heart,
  CheckCircle,
  Calendar,
  TrendingUp,
  Shield,
  Handshake
} from "lucide-react";

const About = () => {
  const milestones = [
    {
      year: "2000s",
      title: "Company Founded",
      description: "SAMAC Technology & Networking Solutions, Inc. was established in Durant, Mississippi"
    },
    {
      year: "2005-2010",
      title: "Expansion Phase",
      description: "Expanded services to include comprehensive network infrastructure and cybersecurity solutions"
    },
    {
      year: "2010-2015",
      title: "Enterprise Growth",
      description: "Began serving large-scale enterprise organizations and government agencies"
    },
    {
      year: "2015-2020",
      title: "Technology Evolution",
      description: "Integrated cloud services and modern IT infrastructure solutions"
    },
    {
      year: "2020-Present",
      title: "Digital Transformation",
      description: "Leading businesses through digital transformation with cutting-edge technology solutions"
    }
  ];

  const values = [
    {
      icon: <Shield className="h-8 w-8" />,
      title: "Reliability",
      description: "We deliver consistent, dependable IT solutions that businesses can count on 24/7."
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: "Expertise",
      description: "Over two decades of experience serving diverse clients from small businesses to government agencies."
    },
    {
      icon: <Heart className="h-8 w-8" />,
      title: "Customer Focus",
      description: "Every solution is tailored to meet the unique needs and challenges of our clients."
    },
    {
      icon: <Handshake className="h-8 w-8" />,
      title: "Partnership",
      description: "We build long-term relationships, serving as trusted technology advisors and partners."
    }
  ];

  const capabilities = [
    "Network Infrastructure Design & Implementation",
    "Cybersecurity Solutions & Threat Management",
    "IT Infrastructure Consulting & Management",
    "Managed IT Services & 24/7 Support",
    "Cloud Migration & Integration Services",
    "Data Center Solutions & Management",
    "Hardware Procurement & Technology Sales",
    "System Integration & Performance Optimization"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20">
      <Navigation currentPage="about" />

      {/* Hero Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4" variant="secondary">
                <Award className="h-4 w-4 mr-2" />
                Over 20 Years of Excellence
              </Badge>
              <h1 className="text-4xl lg:text-6xl font-bold text-foreground mb-6">
                About <span className="bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">SAMAC Technology</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                For over two decades, SAMAC Technology & Networking Solutions, Inc. has been a trusted 
                partner in delivering comprehensive IT solutions to businesses and government agencies 
                across Mississippi and beyond.
              </p>
              <div className="flex items-center space-x-6 text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  <span>Durant, Mississippi</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  <span>Established 2000s</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1581091877018-dac6a371d50f?w=600&auto=format&fit=crop&q=80" 
                alt="Professional IT team collaboration"
                className="rounded-2xl shadow-2xl w-full aspect-square object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-1">20+</div>
                  <div className="text-sm text-muted-foreground">Years of Service</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-r from-secondary/50 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <Target className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">Our Mission</CardTitle>
                </div>
                <CardDescription className="text-base leading-relaxed">
                  To deliver comprehensive, reliable, and innovative IT solutions that empower businesses 
                  and government agencies to achieve their goals through technology. We are committed to 
                  providing exceptional service, building lasting partnerships, and staying at the forefront 
                  of technological advancement.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center space-x-3 mb-4">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <Eye className="h-6 w-6 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">Our Vision</CardTitle>
                </div>
                <CardDescription className="text-base leading-relaxed">
                  To be the leading technology partner in the region, recognized for our expertise, 
                  reliability, and commitment to client success. We envision a future where every 
                  organization we serve leverages technology as a strategic advantage to drive growth, 
                  efficiency, and innovation.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Company Timeline */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Our Journey
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Two decades of growth, innovation, and commitment to excellence in IT services.
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-primary/20"></div>
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex items-center ${index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`w-1/2 ${index % 2 === 0 ? 'pr-8 text-right' : 'pl-8 text-left'}`}>
                    <Card className="border-0 shadow-lg">
                      <CardHeader>
                        <Badge className="w-fit mb-2" variant="secondary">
                          {milestone.year}
                        </Badge>
                        <CardTitle className="text-xl">{milestone.title}</CardTitle>
                        <CardDescription className="text-base">
                          {milestone.description}
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  </div>
                  <div className="relative z-10 flex items-center justify-center w-12 h-12 bg-primary rounded-full border-4 border-white shadow-lg">
                    <TrendingUp className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <div className="w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-gradient-to-r from-secondary/50 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              The principles that guide our work and define our commitment to excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="text-center border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
                <CardHeader>
                  <div className="bg-primary/10 p-4 rounded-lg w-fit mx-auto mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                    {value.icon}
                  </div>
                  <CardTitle className="text-xl">{value.title}</CardTitle>
                  <CardDescription className="text-base">
                    {value.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
                Our Capabilities
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                We offer a comprehensive range of IT services designed to meet the diverse 
                needs of our clients, from small local businesses to large government agencies.
              </p>
              <div className="grid gap-3">
                {capabilities.map((capability, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-muted-foreground">{capability}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1506399309177-3b43e99fead2?w=600&auto=format&fit=crop&q=80" 
                alt="Advanced server infrastructure"
                className="rounded-2xl shadow-2xl w-full aspect-square object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Partner with SAMAC Technology
          </h2>
          <p className="text-xl mb-8 text-primary-foreground/90">
            Join the hundreds of businesses and government agencies who trust us with their 
            technology infrastructure. Let's discuss how we can help your organization thrive.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-white/90">
              <Link to="/contact" className="flex items-center">
                <Phone className="mr-2 h-5 w-5" />
                Contact Us Today
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
              <Link to="/services" className="flex items-center">
                View Our Services
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;